import { IconFromSet } from "@gettactic/components";
import { getScheduleUser } from "@gettactic/helpers/src/schedules/getScheduleUser";
import Konva from "konva";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { Circle, Group, Image as KonvaImage, Line, Rect } from "react-konva";
import { MapInteractiveZone, SpotShape } from "../../../../types";
import { computeAutomaticStateIconCenterOffset } from "../../../../utils/stateIconCentering";
import { loadColoredSvgMarkupImage } from "../../../../utils/svgAssetCache";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH,
  DEFAULT_NODE_RECT_COLOR,
  DEFAULT_STATE_ICON_COLOR
} from "../../defines";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { SpotLayout } from "../../types";
import { CircleProfilePhoto } from "./CircleProfilePhoto";
import { getInteractiveZoneStyles } from "./helpers";

/** Guide color for the editor-only reference circle/crosshair/bounding box
 *  — matches the dashed blue used elsewhere for editor-only overlays (e.g.
 *  the highlight-area preview in SpotShapeItem.tsx). */
const PREVIEW_GUIDE_COLOR = "#2563eb";

export interface InteractiveZoneProps {
  shape: SpotShape;
  layout: SpotLayout;
  interactiveZone?: MapInteractiveZone;
  onClick: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  onMouseEnter?: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  onMouseLeave?: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  selectIndex?: number;
  isActive?: boolean;
  isSpotHovered?: boolean;
}

export const InteractiveZone: React.FC<InteractiveZoneProps> = ({
  shape,
  layout,
  interactiveZone,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selectIndex,
  isActive,
  isSpotHovered
}) => {
  const { isPrivacyMode, authenticatedUser, isViewMode } = useMapCanvas();
  const assignedUser = interactiveZone?.assignedUser || null;
  const actualSchedule = interactiveZone?.actualSchedule || null;
  const scheduleUser = getScheduleUser(actualSchedule);
  // A walk-up check-in with no real person attached is stamped with a
  // "walkup" placeholder identity — nothing to show an avatar for, the
  // plain occupied dot already says enough.
  const isWalkUpPlaceholderUser = !!scheduleUser?.roles?.includes("walkup");

  const circleStyle = getInteractiveZoneStyles({
    isSelected: interactiveZone?.isSelected || false,
    isLastAccessed: interactiveZone?.isLastUsed || false,
    isOccupied: interactiveZone?.isOccupied || false,
    notAssignedResource: interactiveZone?.notAssigned || false,
    notAssignedUser: !assignedUser,
    requiresApproval: interactiveZone?.requiresApproval || false,
    isWalkUp: interactiveZone?.isWalkUp || false,
    hasSchedules: interactiveZone?.hasSchedules || false,
    // showCircleProfilePhoto normally makes the dot transparent so the
    // avatar rendered below shows through it — but for the walk-up
    // placeholder we suppress that avatar, so the dot must stay opaque
    // (its own occupied color) instead of going invisible.
    showCircleProfilePhoto:
      (interactiveZone?.showCircleProfilePhoto || false) &&
      !isWalkUpPlaceholderUser,
    hasCheckedIn: interactiveZone?.actualSchedule?.has_checked_in || false
  });

  const circleStrokeWidth =
    shape.circleProps?.strokeWidth ||
    //circleStyle.strokeWidth ||
    DEFAULT_NODE_CIRCLE_STROKE_WIDTH;
  const circleRadius = shape.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;

  const circleColor =
    selectIndex !== undefined
      ? "#2563eb"
      : isSpotHovered
        ? "#828383"
        : circleStyle.fill;

  // Editor-only live preview of the configured state icon, positioned with
  // the exact same offset/size math as the SVG viewer (InteractiveZoneSVG)
  // — lets the Icon X/Y/Size panel controls be understood directly on the
  // spot instead of only in the isolated panel preview. There's no real
  // occupied/available state while editing, so this always prefers the
  // occupied icon when both are set.
  const previewIcon =
    !isViewMode && isActive
      ? shape.stateIcons?.occupied || shape.stateIcons?.available
      : undefined;
  // Matches InteractiveZoneSVG: parking rects are sized to the spot's own
  // configured width/height (grid tool cells can be much smaller than the
  // desk-sized default), so clamp the rendered size to the rect's bounds for
  // parking specifically — otherwise this preview would show an icon fitting
  // fine while the real viewer clamps it, contradicting the "exact same
  // math" this preview exists to guarantee. Also matches InteractiveZoneSVG
  // in preferring the spot's own highlight-area rect (what's actually
  // painted as the cell) over `layout.rect` when the two disagree. This is
  // about the spot's own grid-cell sizing, not how it's currently rendered —
  // a Default-appearance parking spot still has a (possibly tiny)
  // parking-sized cell, so this keys off the spot's actual type, not its
  // effective appearance.
  const isPreviewParking =
    shape.spotType === "parking_lot" || shape.spotType === "parking_space";
  const previewBoundsWidth =
    shape.stateArea?.kind === "rect" && shape.stateArea.rect?.width
      ? shape.stateArea.rect.width
      : layout.rect.width;
  const previewBoundsHeight =
    shape.stateArea?.kind === "rect" && shape.stateArea.rect?.height
      ? shape.stateArea.rect.height
      : layout.rect.height;
  // Matches InteractiveZoneSVG: leave a visible margin instead of clamping
  // flush to the rect's edges.
  const PARKING_ICON_FIT_RATIO = 0.7;
  const configuredPreviewIconSize = shape.stateIcons?.size || circleRadius * 2;
  const previewIconSize =
    isPreviewParking && previewBoundsWidth && previewBoundsHeight
      ? Math.min(
          configuredPreviewIconSize,
          previewBoundsWidth * PARKING_ICON_FIT_RATIO,
          previewBoundsHeight * PARKING_ICON_FIT_RATIO
        )
      : configuredPreviewIconSize;
  // Matches InteractiveZoneSVG: when neither offset has been customized AND
  // a highlight area is configured, default to centering on that area
  // instead of the dot — without this, the editor preview shows the icon at
  // the dot's position even though the real viewer auto-centers it,
  // misleading whoever is placing it until they save.
  const hasCustomIconOffset =
    shape.stateIcons?.offsetX !== undefined ||
    shape.stateIcons?.offsetY !== undefined;
  const autoCenterOffset = !hasCustomIconOffset
    ? computeAutomaticStateIconCenterOffset(shape)
    : null;
  // Matches InteractiveZoneSVG: a spot type with no rect of its own (Node)
  // has nothing that naturally scales/centers with a configured highlight
  // area — center the dot itself on the area in that case (e.g. a grid tool
  // cell), the same way the state icon already does.
  const hasOwnRect = !!(layout.rect.width && layout.rect.height);
  const dotCenterOffset = !hasOwnRect
    ? computeAutomaticStateIconCenterOffset(shape)
    : null;
  const dotOffsetX = dotCenterOffset?.offsetX ?? 0;
  const dotOffsetY = dotCenterOffset?.offsetY ?? 0;
  const dotLayout: SpotLayout =
    dotOffsetX || dotOffsetY
      ? {
          ...layout,
          circle: {
            x: layout.circle.x + dotOffsetX,
            y: layout.circle.y + dotOffsetY
          }
        }
      : layout;
  // When the dot itself already auto-centered on the highlight area above
  // (no own rect), the icon sitting at the dot's position is already
  // centered too — adding the auto-center delta again on top would double
  // it (see InteractiveZoneSVG).
  const previewIconOffsetX =
    shape.stateIcons?.offsetX ??
    (hasOwnRect ? (autoCenterOffset?.offsetX ?? 0) : 0);
  const previewIconOffsetY =
    shape.stateIcons?.offsetY ??
    (hasOwnRect ? (autoCenterOffset?.offsetY ?? 0) : 0);
  // The dot's own fill isn't a good icon color: some availability states
  // pair a near-white fill with a colored stroke for contrast — a ring the
  // icon doesn't have (see InteractiveZoneSVG). A configured
  // `stateIcons.color` wins; otherwise fall back to one fixed,
  // always-legible neutral, matching the real viewer.
  const previewIconColor = shape.stateIcons?.color || DEFAULT_STATE_ICON_COLOR;

  // With hideDot, the state area is the availability indicator: hide the
  // dot in the editor and in zone-driven view renders. A configured state
  // icon has the same effect everywhere it actually renders (see
  // InteractiveZoneSVG), so the preview hides the dot the same way — a
  // colored dot sitting behind the icon here but not in production would
  // be a misleading preview. The circle keeps a transparent (or, with a
  // configured backdrop, colored) fill so it stays click-/hit-detectable.
  const isDotHidden =
    (shape.stateArea?.hideDot === true && (!isViewMode || !!interactiveZone)) ||
    !!previewIcon;
  // The icon's backdrop is normally fully transparent — `occupiedBackgroundColor`
  // gives it a colored disc instead, but only while previewing the occupied
  // icon specifically (matches `previewIcon` above, which prefers occupied
  // when both are set) — otherwise a spot with only an "available" icon
  // configured would show the occupied backdrop unconditionally, which
  // doesn't happen anywhere it actually renders (InteractiveZoneSVG gates
  // it on the real `interactiveZone.isOccupied`).
  const previewIconBackgroundColor =
    previewIcon && shape.stateIcons?.occupied
      ? shape.stateIcons?.occupiedBackgroundColor
      : undefined;

  const [previewIconImage, setPreviewIconImage] =
    React.useState<HTMLImageElement | null>(null);

  React.useEffect(() => {
    if (!previewIcon) {
      setPreviewIconImage(null);
      return;
    }
    let cancelled = false;
    // Same props as InteractiveZoneSVG's live render (icon, style width/
    // height, preserveAspectRatio) — anything less than an exact prop match
    // risks IconFromSet resolving a different width/height attribute or
    // viewBox fit for some icon set (its `tactic`/`mdi`/`lucide` branches
    // each derive sizing differently), which would rasterize the glyph
    // off-center relative to the live SVG version even though the
    // KonvaImage/translate math positioning it afterward is identical.
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <IconFromSet
        icon={previewIcon}
        color={previewIconColor}
        style={{ width: previewIconSize, height: previewIconSize }}
        preserveAspectRatio="xMidYMid meet"
      />
    );
    // IconFromSet doesn't bake a real color into every icon set the same
    // way — mdi/lucide put it directly on the painted element, but tactic
    // icons paint their inner paths with a literal `currentColor` that only
    // resolves via CSS inheritance from an ancestor. That inheritance isn't
    // reliable once this markup is rasterized standalone (blob image, no
    // live DOM), so replace with the real resolved color ourselves instead
    // of assuming IconFromSet already did it — this covers every set.
    loadColoredSvgMarkupImage(svgString, previewIconColor).then((img) => {
      if (cancelled) return;
      setPreviewIconImage(img);
      getWindowKonvaObject().stages?.[0]?.fire?.("contentChange");
    });
    return () => {
      cancelled = true;
    };
  }, [previewIcon?.set, previewIcon?.icon, previewIconColor, previewIconSize]);

  return (
    <Group opacity={circleStyle.fillOpacity}>
      <Circle
        id={`shape-${shape.id}-circle`}
        // When standing in as the occupied icon's backdrop (not the plain
        // dot), move/resize with the icon — it can be offset or
        // auto-centered away from the dot's own position (see
        // InteractiveZoneSVG for the matching fix).
        x={
          previewIconBackgroundColor
            ? dotLayout.circle.x + previewIconOffsetX
            : dotLayout.circle.x
        }
        y={
          previewIconBackgroundColor
            ? dotLayout.circle.y + previewIconOffsetY
            : dotLayout.circle.y
        }
        radius={previewIconBackgroundColor ? previewIconSize / 2 : circleRadius}
        fill={
          isDotHidden
            ? previewIconBackgroundColor || "transparent"
            : isActive
              ? circleColor
              : shape.isInactive
                ? shape.rectProps?.color || DEFAULT_NODE_RECT_COLOR
                : "grey"
        }
        stroke={isDotHidden ? undefined : circleStyle.stroke}
        strokeWidth={isDotHidden ? 0 : circleStrokeWidth}
        dash={isDotHidden ? undefined : circleStyle.dash}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTap={onClick}
        perfectDrawEnabled={false}
      />
      {previewIcon ? (
        <>
          {/* Reference guide (editor-only, non-interactive): marks the
              dot's original center + radius so the X/Y offset controls have
              a fixed frame of reference instead of the icon floating with
              nothing to judge its position against. */}
          <Circle
            x={dotLayout.circle.x}
            y={dotLayout.circle.y}
            radius={circleRadius}
            stroke={PREVIEW_GUIDE_COLOR}
            strokeWidth={1}
            dash={[4, 3]}
            listening={false}
            perfectDrawEnabled={false}
          />
          {/* Center crosshair — a "+", not a filled circle, so it can never
              read as a second availability dot sitting behind the icon. */}
          <Line
            points={[
              dotLayout.circle.x - 4,
              dotLayout.circle.y,
              dotLayout.circle.x + 4,
              dotLayout.circle.y
            ]}
            stroke={PREVIEW_GUIDE_COLOR}
            strokeWidth={1}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Line
            points={[
              dotLayout.circle.x,
              dotLayout.circle.y - 4,
              dotLayout.circle.x,
              dotLayout.circle.y + 4
            ]}
            stroke={PREVIEW_GUIDE_COLOR}
            strokeWidth={1}
            listening={false}
            perfectDrawEnabled={false}
          />
          {/* Dashed bounding box for the icon's exact footprint — legible
              even before the raster image finishes loading, and regardless
              of how much transparent padding the glyph itself has. */}
          <Rect
            x={dotLayout.circle.x - previewIconSize / 2 + previewIconOffsetX}
            y={dotLayout.circle.y - previewIconSize / 2 + previewIconOffsetY}
            width={previewIconSize}
            height={previewIconSize}
            stroke={PREVIEW_GUIDE_COLOR}
            strokeWidth={1}
            dash={[4, 3]}
            listening={false}
            perfectDrawEnabled={false}
          />
        </>
      ) : null}
      {previewIconImage ? (
        <KonvaImage
          image={previewIconImage}
          x={dotLayout.circle.x - previewIconSize / 2 + previewIconOffsetX}
          y={dotLayout.circle.y - previewIconSize / 2 + previewIconOffsetY}
          width={previewIconSize}
          height={previewIconSize}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : null}
      {assignedUser && !isDotHidden ? (
        <CircleProfilePhoto
          privacy={
            isPrivacyMode && assignedUser.id !== authenticatedUser?.user?.id
          }
          profilePhoto={interactiveZone?.assignedUser?.profile_photo_url || ""}
          radius={circleRadius - circleStrokeWidth / 2}
          layout={dotLayout}
          name={assignedUser.name || ""}
          // Dedicated resources show their person at full strength — the
          // faded look is only for regular restricted assignments.
          restricted={!interactiveZone?.isDedicated}
        />
      ) : null}
      {interactiveZone?.showCircleProfilePhoto &&
      !isDotHidden &&
      !isWalkUpPlaceholderUser ? (
        <CircleProfilePhoto
          privacy={
            isPrivacyMode && scheduleUser?.id !== authenticatedUser?.user?.id
          }
          profilePhoto={scheduleUser?.profile_photo_url ?? ""}
          radius={circleRadius - circleStrokeWidth / 2}
          layout={dotLayout}
          name={scheduleUser?.name || ""}
        />
      ) : null}
    </Group>
  );
};
